// src/components/CreateConfigModal.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { useNotification, notifyPermissionError } from '@1823-partners/core';
import { createConfig, fetchScenarios, fetchBooks, fetchCusips, fetchLatestAsofdt } from "../api";
import dayjs from "dayjs";
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { notifyUngatedFailure } from '../utils/notifyDenial';

const CreateConfigModal = ({ open, onClose, onCreated, userId, initialData }) => {
  const { showNotification } = useNotification();
  const defaultForm = {
    ID: "",
    SCEN_IDS: [],
    CUSIPS: [],
    ASOFDT: "",
    ENV: "UAT",
    BOOK: "",
    DESCRIPTION: "",
    SKIP_INTEX: false,
    SKIP_ADCO: false,
    IA_ONLY: false,
    HELLO_WORLD: false,
    CUSTOM_PM_BRANCH: "",
    CUSTOM_PKWA_BRANCH: "",
    SAVE: false,
    MODIFIED_BY: userId,
  };

  const [formData, setFormData] = useState(initialData || defaultForm);
  const [loading, setLoading] = useState(false);
  const [availableScenarios, setAvailableScenarios] = useState([]);
  const [availableCusips, setAvailableCusips] = useState([]);
  const [availableBooks, setAvailableBooks] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [cusipsLoading, setCusipsLoading] = useState(false);
  const [scenariosLoading, setScenariosLoading] = useState(false);

  const [latestDateLoading, setLatestDateLoading] = useState(false);
  const [dateMessage, setDateMessage] = useState('');
  const dateEdited = useRef(false);

  const DB_ENV = process.env.REACT_APP_DB_ENV || 'uat';

  useEffect(() => {
    let active = true;
    setAvailableScenarios([]);
    setAvailableBooks([]);
    setAvailableCusips([]);
    // Don't fetch if modal is closed or required fields are missing
    if (!open || !formData.ENV || !formData.ASOFDT) {
      return;
    }

    // Set loading states
    setScenariosLoading(true);
    setCusipsLoading(true);

    // Debounce API calls by 500ms to prevent excessive requests
    const timeoutId = setTimeout(async () => {
      try {
        // Fetch all data in parallel
        const [scenariosResponse, booksResponse, cusipsResponse] = await Promise.all([
          fetchScenarios(formData.ENV, formData.ASOFDT).catch(err => {
            console.error("Error fetching scenarios", err);
            notifyUngatedFailure(showNotification, err);
            return { data: [] };
          }),
          fetchBooks(formData.ENV, formData.ASOFDT).catch(err => {
            console.error("Error fetching books", err);
            notifyUngatedFailure(showNotification, err);
            return { data: [] };
          }),
          fetchCusips(formData.ENV, formData.ASOFDT).catch(err => {
            console.error("Error fetching cusips", err);
            notifyUngatedFailure(showNotification, err);
            return { data: [] };
          })
        ]);

        if (!active) return;
        setAvailableScenarios(scenariosResponse.data || []);
        setAvailableBooks(booksResponse.data || []);
        setAvailableCusips(cusipsResponse.data || []);
      } catch (error) {
        console.error("Error loading data", error);
      } finally {
        if (active) {
          setScenariosLoading(false);
          setCusipsLoading(false);
        }
      }
    }, 500);

    return () => {
      active = false;
      clearTimeout(timeoutId);
      setScenariosLoading(false);
      setCusipsLoading(false);
    };
  }, [open, formData.ENV, formData.ASOFDT]);

  // When initialData changes (i.e. when editing an existing config), update the form.
  // Also reset form when modal opens without initial data (creating new config)
  useEffect(() => {
    if (!open) {
      return; // Don't update form when modal is closed
    }

    let active = true;
    dateEdited.current = false;
    setFormErrors({});
    setDateMessage('');
    setLatestDateLoading(false);

    if (initialData) {
      // If SCEN_IDS is a string, split it into an array; otherwise assume it's already an array.
      // Handle null/undefined values by defaulting to empty array
      const parsedScenIds = initialData.SCEN_IDS
        ? (typeof initialData.SCEN_IDS === "string"
            ? initialData.SCEN_IDS.split(",").filter(id => id.trim())
            : Array.isArray(initialData.SCEN_IDS)
              ? initialData.SCEN_IDS
              : [])
        : [];

      const parsedCusips = initialData.CUSIPS
        ? (typeof initialData.CUSIPS === "string"
            ? initialData.CUSIPS.split(",").filter(cusip => cusip.trim())
            : Array.isArray(initialData.CUSIPS)
              ? initialData.CUSIPS
              : [])
        : [];

      const formattedDate = initialData.ASOFDT ? formatDate(initialData.ASOFDT) : "";

      setFormData({
        ...initialData,
        SCEN_IDS: parsedScenIds,
        CUSIPS: parsedCusips,
        ASOFDT: formattedDate
      });
    } else {
      // Reset to empty form when creating new config
      setFormData({
        ...defaultForm,
        SCEN_IDS: [],  // Explicitly ensure empty
        CUSIPS: [],    // Explicitly ensure empty
        MODIFIED_BY: userId
      });
      setLatestDateLoading(true);
      fetchLatestAsofdt('UAT')
        .then(({ data }) => {
          if (!active || dateEdited.current) return;
          if (data?.asofdt) {
            setFormData(previous => ({ ...previous, ASOFDT: data.asofdt }));
          } else {
            setDateMessage('No active forward-rate date is available in UAT. Enter an as-of date.');
          }
        })
        .catch(() => {
          if (active && !dateEdited.current) {
            setDateMessage('Unable to load the latest UAT forward-rate date. Enter an as-of date.');
          }
        })
        .finally(() => {
          if (active) setLatestDateLoading(false);
        });
    }
    return () => { active = false; };
  }, [initialData, open]);


  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'ASOFDT') {
      dateEdited.current = true;
      setDateMessage('');
      setLatestDateLoading(false);
    }
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });

    // Clear field-specific errors when user makes changes
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";

    const date = dayjs(dateStr);
    return date.format("YYYY-MM-DD");
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.SCEN_IDS || !Array.isArray(formData.SCEN_IDS) || formData.SCEN_IDS.length === 0) {
      errors.SCEN_IDS = 'At least one scenario is required';
    }

    if (!formData.ASOFDT) {
      errors.ASOFDT = 'As of date is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      console.log('Form validation failed');
      return;
    }

    setLoading(true);

    // Built inside the try: a throw here would otherwise skip both the catch and
    // setLoading(false), leaving the modal spinning with no error shown.
    try {
      const asofdtFormatted = formatDate(formData.ASOFDT);

      // Join selected scenario IDs and CUSIPs into comma-separated strings
      const scenIds = formData.SCEN_IDS?.length > 0 ? formData.SCEN_IDS.join(",") : null;
      const cusips = formData.CUSIPS?.length > 0 ? formData.CUSIPS.join(",") : null;

      // Prepare data with null for empty/blank fields. String() because these are
      // spread from a fetched config, whose values are not guaranteed to be strings —
      // .trim() on a number throws, and ?. only guards null/undefined.
      const dataToSubmit = {
        ...formData,
        SCEN_IDS: scenIds,
        CUSIPS: cusips,
        ASOFDT: asofdtFormatted || null,
        BOOK: String(formData.BOOK ?? "").trim() || null,
        DESCRIPTION: String(formData.DESCRIPTION ?? "").trim() || null,
        ID: String(formData.ID ?? "").trim(),
        // HELLO_WORLD needs the IA run this job performs: only with Intex ADCO Only
        // and never when Intex is skipped. Stored values that break that are dropped.
        HELLO_WORLD: Boolean(formData.HELLO_WORLD) && Boolean(formData.IA_ONLY) && !formData.SKIP_INTEX,
        mode: initialData ? "edit" : "create",
      };

      await createConfig(dataToSubmit);
      onCreated && onCreated();
      onClose();
      setFormData(defaultForm);
    } catch (error) {
      console.error("Error creating/updating config", error);
      if (!notifyPermissionError(showNotification, error)) {
        const status = error.response?.status;
        const errorData = [400, 404, 409].includes(status)
          ? error.response?.data
          : null;
        const configError = errorData?.error || null;
        if (configError && errorData?.field) {
          setFormErrors((previous) => ({
            ...previous,
            [errorData.field]: configError,
          }));
        }
        showNotification(configError || "Error creating/updating config", "error");
      }
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        {initialData ? "Edit Scenario Config" : "Create New Scenario Config"}
      </DialogTitle>
      <form onSubmit={handleSubmit} noValidate>
        <DialogContent dividers>
          <TextField
            fullWidth
            margin="normal"
            label="Config ID"
            name="ID"
            value={formData.ID || ""}
            onChange={handleChange}
            InputProps={{ readOnly: Boolean(initialData) }}
            error={!!formErrors.ID}
            helperText={
              formErrors.ID ||
              (initialData
                ? "Config IDs cannot be renamed"
                : "Optional - leave blank to generate a timestamp ID")
            }
          />
          <FormControl fullWidth margin="normal">
            <InputLabel id="env-label">Environment</InputLabel>
            <Select
              labelId="env-label"
              label="Environment"
              name="ENV"
              value={formData.ENV}
              onChange={handleChange}
            >
              <MenuItem value="DEV">DEV</MenuItem>
              <MenuItem value="UAT">UAT</MenuItem>
              {DB_ENV === 'prod' && <MenuItem value="PROD">PROD</MenuItem>}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            margin="normal"
            label="Asof Date"
            name="ASOFDT"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={formData.ASOFDT || ""}
            onChange={handleChange}
            required
            error={!!formErrors.ASOFDT}
            helperText={formErrors.ASOFDT || (latestDateLoading ? 'Loading latest UAT forward-rate date...' : dateMessage)}
          />
          {/* Autocomplete for searchable multi-select of Scenario IDs */}
          <Autocomplete
            multiple
            options={['SELECT_ALL', ...(availableScenarios || []).sort()]}
            getOptionLabel={(option) => option === 'SELECT_ALL' ? 'Select All' : (option || '')}
            value={formData.SCEN_IDS || []}
            loading={scenariosLoading}
            loadingText="Loading scenarios..."
            noOptionsText={scenariosLoading ? "Loading..." : "No scenarios available"}
            onChange={(event, newValue, reason) => {
              // Check if "Select All" was clicked
              if (newValue.includes('SELECT_ALL')) {
                // If Select All is now in the array, select everything except SELECT_ALL itself
                const allScenarios = (availableScenarios || []).sort();
                setFormData({ ...formData, SCEN_IDS: allScenarios });
              } else {
                setFormData({ ...formData, SCEN_IDS: newValue || [] });
              }

              // Clear scenarios error when user selects scenarios
              if (formErrors.SCEN_IDS) {
                setFormErrors(prev => ({
                  ...prev,
                  SCEN_IDS: null
                }));
              }
            }}
            renderOption={(props, option, { selected }) => {
              // Special handling for Select All option
              if (option === 'SELECT_ALL') {
                // Only show as checked if there are items AND all are selected
                const allSelected = availableScenarios.length > 0 &&
                                   formData.SCEN_IDS?.length === availableScenarios.length;
                return (
                  <li {...props} style={{ fontWeight: 'bold', borderBottom: '1px solid #ddd' }}>
                    <Checkbox
                      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      style={{ marginRight: 8 }}
                      checked={allSelected}
                    />
                    Select All
                  </li>
                );
              }

              return (
                <li {...props}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option}
                </li>
              );
            }}
            disableCloseOnSelect={true}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Scenarios"
                margin="normal"
                required
                error={!!formErrors.SCEN_IDS}
                helperText={formErrors.SCEN_IDS || "Select one or more scenarios"}
              />
            )}
          />
          <Autocomplete
            multiple
            freeSolo
            options={['SELECT_ALL', ...(availableCusips || []).sort()]}
            getOptionLabel={(option) => option === 'SELECT_ALL' ? 'Select All' : (option || '')}
            value={formData.CUSIPS || []}
            loading={cusipsLoading}
            loadingText="Loading CUSIPs..."
            noOptionsText={cusipsLoading ? "Loading..." : "No CUSIPs available"}
            onChange={(event, newValue) => {
              // Check if "Select All" was clicked
              if (newValue.includes('SELECT_ALL')) {
                // If Select All is now in the array, select everything except SELECT_ALL itself
                const allCusips = (availableCusips || []).sort();
                setFormData({ ...formData, CUSIPS: allCusips });
              } else {
                setFormData({ ...formData, CUSIPS: newValue || [] });
              }
            }}
            filterOptions={(options, { inputValue }) => {
              // Fast filtering for large datasets
              if (!inputValue) {
                // When no search, limit to first 100 items for performance
                return options.slice(0, 101);
              }

              const searchLower = inputValue.toLowerCase();
              const filtered = [];

              // Always include SELECT_ALL at top if it matches search
              if ('select all'.includes(searchLower)) {
                filtered.push('SELECT_ALL');
              }

              // Filter through CUSIPs efficiently, limit results to 200
              for (let i = 1; i < options.length && filtered.length < 201; i++) {
                if (options[i].toLowerCase().includes(searchLower)) {
                  filtered.push(options[i]);
                }
              }

              return filtered;
            }}
            ListboxProps={{
              style: { maxHeight: '400px' }
            }}
            renderOption={(props, option, { selected }) => {
              // Special handling for Select All option
              if (option === 'SELECT_ALL') {
                // Only show as checked if there are items AND all are selected
                const allSelected = availableCusips.length > 0 &&
                                   formData.CUSIPS?.length === availableCusips.length;
                return (
                  <li {...props} style={{ fontWeight: 'bold', borderBottom: '1px solid #ddd' }}>
                    <Checkbox
                      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      style={{ marginRight: 8 }}
                      checked={allSelected}
                    />
                    Select All ({availableCusips.length} items)
                  </li>
                );
              }

              return (
                <li {...props}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option}
                </li>
              );
            }}
            disableCloseOnSelect={true}
            renderInput={(params) => (
              <TextField
                {...params}
                label={`CUSIPs (Optional)${availableCusips.length > 0 ? ` - ${availableCusips.length} total` : ''}`}
                margin="normal"
                helperText={availableCusips.length > 100 ? "Type to search through all CUSIPs. Select All will select all " + availableCusips.length + " CUSIPs." : ""}
              />
            )}
          />
          <Autocomplete
            freeSolo
            forcePopupIcon
            inputValue={formData.BOOK || ''}
            onInputChange={(event, newValue, reason) => {
              if (reason === 'input' || reason === 'clear') {
                setFormData(previous => ({ ...previous, BOOK: newValue }));
              }
            }}
            options={(availableBooks || []).sort()}
            getOptionLabel={(option) => option || ''}
            value={formData.BOOK || null}
            onChange={(event, newValue) =>
              setFormData(previous => ({ ...previous, BOOK: newValue || '' }))
            }
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option}
              </li>
            )}
            disableCloseOnSelect={true}
            renderInput={(params) => (
              <TextField {...params} label="Book (Optional)" margin="normal"
                helperText="Select a book with positive holdings for this environment/date, or type a book name." />
            )}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Description"
            name="DESCRIPTION"
            multiline
            rows={4}
            value={formData.DESCRIPTION}
            onChange={handleChange}
          />
          <FormControlLabel
            control={
              <Checkbox
                name="SKIP_INTEX"
                checked={formData.SKIP_INTEX || false}
                disabled={formData.IA_ONLY || false}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    SKIP_INTEX: e.target.checked,
                    IA_ONLY: false,
                    HELLO_WORLD: false
                  });
                }}
              />
            }
            label="Skip Intex"
          />
          <FormControlLabel
            control={
              <Checkbox
                name="SKIP_ADCO"
                checked={formData.SKIP_ADCO || false}
                disabled={formData.IA_ONLY || false}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    SKIP_ADCO: e.target.checked,
                    IA_ONLY: false
                  });
                }}
              />
            }
            label="Skip ADCO"
          />
          <FormControlLabel
            control={
              <Checkbox
                name="IA_ONLY"
                checked={formData.IA_ONLY || false}
                disabled={(formData.SKIP_INTEX || false) || (formData.SKIP_ADCO || false)}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    IA_ONLY: e.target.checked,
                    SKIP_INTEX: false,
                    SKIP_ADCO: false,
                    HELLO_WORLD: e.target.checked ? (formData.HELLO_WORLD || false) : false
                  });
                }}
              />
            }
            label="Intex ADCO Only"
          />
          <FormControlLabel
            control={
              <Checkbox
                name="HELLO_WORLD"
                checked={formData.HELLO_WORLD || false}
                disabled={!(formData.IA_ONLY || false) || (formData.SKIP_INTEX || false)}
                onChange={handleChange}
              />
            }
            label="Hello World"
          />
          <TextField
            fullWidth
            margin="normal"
            label="Custom PAM-MASTER Branch"
            name="CUSTOM_PM_BRANCH"
            value={formData.CUSTOM_PM_BRANCH || ''}
            onChange={handleChange}
            placeholder="pam-master branch name"
          />
          <TextField
            fullWidth
            margin="normal"
            label="Custom PAM-KWA Branch"
            name="CUSTOM_PKWA_BRANCH"
            value={formData.CUSTOM_PKWA_BRANCH || ''}
            onChange={handleChange}
            placeholder="pam-kwa branch name"
          />
          <FormControlLabel
            control={
              <Checkbox
                name="SAVE"
                checked={formData.SAVE}
                onChange={handleChange}
              />
            }
            label="Save Results"
          />
          <TextField
            fullWidth
            margin="normal"
            label="Modified By"
            name="MODIFIED_BY"
            value={formData.MODIFIED_BY}
            disabled
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading} sx={{ color: "black" }}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !formData.SCEN_IDS || !Array.isArray(formData.SCEN_IDS) || formData.SCEN_IDS.length === 0 || !formData.ASOFDT}
            sx={{ backgroundColor: "lightgrey", color: "black" }}
          >
            {loading
              ? initialData
                ? "Updating..."
                : "Creating..."
              : initialData
              ? "Update Config"
              : "Create Config"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateConfigModal;
